<template>
  <div class="table-wrapper">
    <ve-table v-if="tableData?.length"
              id="rta-table"
              :table-data="copyData"
              :columns="computedHeaders"
              :column-hidden-option="tableOptions.columnHiddenOption"
              :column-width-resize-option="tableOptions.columnWidthResizeOption"
              :checkbox-option="tableOptions.checkboxOption"
              :sort-option="tableOptions.sortOption"
              :virtual-scroll-option="tableOptions.virtualScrollOption"
              :event-custom-option="tableOptions.eventCustomOption"
              :fixed-header="true"
              :border-x="true"
              :border-y="true"
              :max-height="maxHeight"
              row-key-field-name="id"
              style="min-width:100%; word-break:break-word"
              :cell-style-option="cellStyleOption"
              :row-style-option="rowStyleOption" />
    <div v-else class="empty-state">
      <div class="empty-state-content">
        <h4>Nenhuma RTA encontrada</h4>
        <p>Não há RTAs para exibir com os filtros aplicados.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TableRTA",
  props: ["tableColumns", "tableData", "dbClickFunc", "openInNewTabFunc", "priorizeFunc", "justifyFunc", "recordFunc", "hideColsArr", "bottomOffset"],
  data() {
    return {
      maxHeight: this.calcMaxHeight(),
      filtersMountedObj: {},
      copyData: [],
      columnFilters: {},
      filterSearch: {},
      dropdownObserver: null,
      observedDropdowns: new Set(),
      observerInterval: null,
      tableOptions: {
        columnHiddenOption: { defaultHiddenColumnKeys: this.hideColsArr },
        columnWidthResizeOption: { enable: true, minWidth: 60 },
        sortOption: {
          multipleSort: true,
          sortChange: (params) => {
            Object.keys(params).forEach(key => {
              this.tableData.sort((a, b) => {
                if (params[key] === "asc") {
                  return ('' + a[key]).localeCompare(b[key], "br", { ignorePunctuation: true });
                } else if (params[key] === "desc") {
                  return ('' + b[key]).localeCompare(a[key], "br", { ignorePunctuation: true });
                } else {
                  return 0;
                }
              });
            });
          }
        },
        virtualScrollOption: { enable: true },
        eventCustomOption: {
          bodyRowEvents: ({ row }) => ({
            dblclick: () => { if (this.dbClickFunc) this.dbClickFunc(row); }
          }),
          bodyCellEvents: ({ row, column }) => ({
            click: (event) => {
              // Verifica se clicou no ícone de abrir em nova aba
              if (column?.field === "actions" && event.target.classList.contains('open-new-tab-icon')) {
                if (this.openInNewTabFunc) {
                  event.stopPropagation();
                  this.openInNewTabFunc(row);
                }
              } else if (row.status == "Pendente" && column?.field === "priorizerta") {
                if (this.priorizeFunc) this.priorizeFunc(row);
              } else if (row.status != "Aberto" && column?.field === "justifyrta") {
                if (this.justifyFunc) this.justifyFunc(row);
              } else if (column?.field === "record" && row.record === "Justificada") {
                if (this.recordFunc) this.recordFunc(row);
              }
            }
          }),
        }
      },
      cellStyleOption: {
        headerCellClass: ({ rowIndex }) => (rowIndex === 0 ? "table-header-cell-class" : ""),
        bodyCellClass({ row, column }) {
          if (column.field === "priorizerta") return row.status == "Pendente" ? "priorizar-btn" : "priorizar-label";
          if (column.field === "justifyrta") return row.status == "Pendente" ? "justificar-btn" : "justificar-label";
          if (column.field === "efficiencyrta") {
            if (row.efficiencyrta === "Reprovado") return "ineficaz-class";
            if (row.efficiencyrta === "Aprovado") return "eficaz-class";
            return "pending-class";
          }
          if (column.field === "efficiencyaction") {
            if (row.efficiencyaction === "Reprovado") return "ineficaz-class";
            if (row.efficiencyaction === "Aprovado") return "eficaz-class";
            return "pending-class";
          }
          if (column.field === "record") {
            if (row.record === "Pendente") return "pending-class";
            if (row.record === "Justificada") return "record-clickable justified-class";
            // Formato timestamp: 14 dígitos (yyyyMMddHHmmss, ex: 20260120143045) ou formato legado: RTA-XXX ou 13 dígitos
            if (row.record && (/^\d{14}$/.test(row.record) || row.record.startsWith("RTA-") || /^\d{13}$/.test(row.record))) return "rta-code-class";
            return "pending-class";
          }
        },
      },
      rowStyleOption: { stripe: true, hoverHighlight: true },
    };
  },

  watch: {
    tableData: {
      handler(newVal) {
        this.copyData = [...newVal];
        // Não reseta columnFilters para manter os filtros aplicados
        this.createFiltersFunction();
        // Reaplica os filtros salvos
        if (Object.keys(this.columnFilters).length > 0) {
          this.applyColumnFilters();
        }
      },
      deep: true,
      immediate: true
    },
    copyData: {
      handler() { this.createFiltersFunction(); },
      deep: true,
      immediate: true
    }
  },

  computed: {
    allChecked() {
      return this.tableData.length > 0 && this.tableData.every(row => row.checked);
    },
    computedHeaders() {
      return this.tableColumns.map(col => {
        const column = { ...col };

        if (col.field === 'checkbox') {
          column.renderHeaderCell = (params, h) => {
            return h('div', {
              style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0'
              }
            }, [
              h('input', {
                attrs: {
                  type: 'checkbox'
                },
                domProps: {
                  checked: this.allChecked
                },
                on: {
                  change: (e) => {
                    this.toggleAllCheckboxes(e.target.checked);
                  }
                }
              })
            ]);
          };
          column.renderBodyCell = ({ row }, h) => {
            return h('div', {
              style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%'
              }
            }, [
              h('input', {
                attrs: {
                  type: 'checkbox'
                },
                domProps: {
                  checked: row.checked || false
                },
                on: {
                  change: (e) => {
                    this.$set(row, 'checked', e.target.checked);
                  }
                }
              })
            ]);
          };
        }

        if (col.field !== 'checkbox' && col.field !== 'priorizerta' && col.field !== 'justifyrta') {
          column.filter = {
            isMultiple: true,
            filterList: this.filtersMountedObj[col.field] || [],
            filteredValue: this.columnFilters[col.field] || [],
            filterConfirm: (filterList) => {
              const labels = filterList.filter(o => o.selected).map(o => o.label);
              this.$set(this.columnFilters, col.field, labels);
              this.applyColumnFilters();
              this.$emit('column-filters-changed', this.columnFilters);
            },
            filterReset: () => {
              this.$delete(this.columnFilters, col.field);
              this.$delete(this.filterSearch, col.field);
              this.applyColumnFilters();
              this.$emit('column-filters-changed', this.columnFilters);
            },
          };
        }

        return column;
      });
    }
  },

  methods: {
    getFilterOptions(field) {
      const options = this.filtersMountedObj[field] || [];
      const term = (this.filterSearch[field] || '').toLowerCase();
      if (!term) return options;
      return options.filter(o => o.label.toLowerCase().includes(term));
    },

    injectFilterSearchInput() {
      // Função para injetar o input de busca em um dropdown
      const injectSearchInput = (dropdown) => {
        // Verificar se já tem o input de busca
        if (dropdown.querySelector('.filter-search-input')) return;

        // Verificar se é um dropdown de filtro múltiplo
        const hasMultipleItems = dropdown.querySelector('.ve-dropdown-items-multiple');
        if (!hasMultipleItems) return;

        // Encontrar o container de itens
        const itemsContainer = dropdown.querySelector('.ve-dropdown-items');
        if (!itemsContainer) return;

        // Criar o wrapper do input
        const searchWrapper = document.createElement('div');
        searchWrapper.className = 'filter-search-wrapper';

        // Criar o input de busca
        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.className = 'filter-search-input';
        searchInput.placeholder = 'Buscar opção';

        // Adicionar evento de input
        searchInput.addEventListener('input', (event) => {
          const searchTerm = event.target.value.toLowerCase().trim();
          const checkboxItems = dropdown.querySelectorAll('.ve-dropdown-items-li');

          checkboxItems.forEach(item => {
            const label = item.querySelector('.ve-checkbox-label');
            if (label) {
              const text = label.textContent.toLowerCase().trim();
              const shouldShow = !searchTerm || text.includes(searchTerm);

              if (shouldShow) {
                item.style.cssText = 'display: flex !important; visibility: visible !important; height: auto !important;';
              } else {
                item.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important;';
              }
            }
          });
        });

        searchWrapper.appendChild(searchInput);

        // Inserir no início do dropdown, antes dos itens
        itemsContainer.insertBefore(searchWrapper, itemsContainer.firstChild);

        // Focar no input
        setTimeout(() => searchInput.focus(), 50);
      };

      // Observar mudanças de atributos (especificamente classe)
      this.$nextTick(() => {
        // Criar observer uma única vez
        this.dropdownObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const element = mutation.target;
              // Verificar se a classe ve-dropdown-dd-show foi adicionada
              if (element.classList.contains('ve-dropdown-dd-show')) {
                injectSearchInput(element);
              }
            }
          });
        });

        // Função para observar dropdowns que ainda não foram observados
        const observeDropdowns = () => {
          const dropdowns = document.querySelectorAll('.ve-dropdown-dd');
          dropdowns.forEach(dropdown => {
            // Criar ID único para o dropdown baseado em sua posição no DOM
            const dropdownId = dropdown.dataset.observerId || Math.random().toString(36);
            dropdown.dataset.observerId = dropdownId;

            // Só observar se ainda não foi observado
            if (!this.observedDropdowns.has(dropdownId)) {
              try {
                this.dropdownObserver.observe(dropdown, {
                  attributes: true,
                  attributeFilter: ['class']
                });
                this.observedDropdowns.add(dropdownId);
              } catch (e) {
                // Ignorar erros se o elemento já estiver sendo observado
              }
            }
          });
        };

        // Observar imediatamente
        observeDropdowns();

        // Re-observar periodicamente para pegar novos dropdowns
        // Usar intervalo maior (2s) para reduzir impacto na performance
        this.observerInterval = setInterval(observeDropdowns, 2000);
      });
    },

    toggleAllCheckboxes(checked) {
      this.tableData.forEach(row => {
        this.$set(row, 'checked', checked);
      });
    },
    applyColumnFilters() {
      let dados = this.tableData.slice();
      Object.entries(this.columnFilters).forEach(([field, labels]) => {
        if (labels?.length) dados = dados.filter(r => labels.includes(r[field]));
      });
      this.copyData = dados;
      this.createFiltersFunction(); 
    },

    resetAllColumnFilters() {
      this.columnFilters = {};
      this.filterSearch = {};
      this.applyColumnFilters();
      this.$emit('column-filters-changed', this.columnFilters);
    },

    loadColumnFilters(savedFilters) {
      if (savedFilters && typeof savedFilters === 'object') {
        this.columnFilters = { ...savedFilters };
        this.applyColumnFilters();
      }
    },

    getColumnFilters() {
      return { ...this.columnFilters };
    },

    searchByNameField(labels, field) {                     
      this.copyData = this.tableData.filter(
        x => labels.length === 0 || labels.includes(x[field])
      );
    },

    createFiltersFunction() {
      const cols = this.tableColumns.map(i => i.field);
      const dataValues = {};
      for (const row of this.copyData) {
        for (const col of cols) {
          if (row[col] !== undefined) {
            const stringValue = String(row[col]);
            const already = dataValues[col]?.some(it => it.label === stringValue);
            if (!already) {
              const selected = (this.columnFilters[col] || []).includes(stringValue);
              const filterObj = {
                value: dataValues[col] ? dataValues[col].length : 0,
                label: stringValue,
                selected
              };
              dataValues[col] = dataValues[col] ? [...dataValues[col], filterObj] : [filterObj];
            }
          }
        }
      }
      this.filtersMountedObj = dataValues;
    },

    calcMaxHeight() {
      const el = this.$el;
      if (!el) return '400px';
      const rect = el.getBoundingClientRect();
      const offset = this.bottomOffset || 100;
      const available = window.innerHeight - rect.top - offset;
      return Math.max(200, available) + 'px';
    },
    onResize() { this.maxHeight = this.calcMaxHeight(); },
  },

  mounted() {
    this.copyData = [...this.tableData];
    this.createFiltersFunction();
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.injectFilterSearchInput();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);

    // Limpar o observer de dropdowns
    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect();
      this.dropdownObserver = null;
    }

    // Limpar o interval
    if (this.observerInterval) {
      clearInterval(this.observerInterval);
      this.observerInterval = null;
    }

    // Limpar o Set de dropdowns observados
    if (this.observedDropdowns) {
      this.observedDropdowns.clear();
    }
  }
};
</script>

<style scoped>
.table-wrapper {
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 12rem;
  margin-top: 8rem;
}

.empty-state-content {
  text-align: center;
  max-width: 500px;
}

.empty-state-content h4 {
  color: #004747;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.empty-state-content p {
  color: #6c757d;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}
</style>
<style>
.filter-search-wrapper {
  padding: 0.2rem 0.4rem;
}

.filter-search-input {
  width: 100%;
  border: 1px solid #c7c7c7;
  border-radius: 0.2rem;
  padding: 0.2rem 0.35rem;
}

/* Fix dropdown filter positioning and sizing - force downward opening */
.ve-dropdown {
    position: relative !important;
}

.ve-dropdown-popper {
    position: fixed !important;
    z-index: 9999 !important;
}

.ve-dropdown-popper[x-placement^="top"] {
    top: auto !important;
    bottom: auto !important;
}

.ve-dropdown-popper[x-placement^="bottom"] {
    top: auto !important;
    bottom: auto !important;
}

.ve-dropdown-items {
    max-height: 400px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    min-width: 250px !important;
    max-width: 500px !important;
}

.ve-dropdown-items > * {
    max-height: none !important;
    overflow: visible !important;
}

.ve-dropdown-items * {
    max-height: none !important;
    overflow: visible !important;
}

.ve-dropdown-items-multiple,
.ve-dropdown-items-multiple *,
.ve-dropdown-items-multiple-content,
.ve-dropdown-items-multiple-content * {
    max-height: none !important;
    overflow: visible !important;
}

.ve-dropdown-items-li {
    white-space: normal !important;
    word-wrap: break-word !important;
    padding: 10px 15px !important;
    line-height: 1.5 !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    overflow: visible !important;
}

.ve-dropdown-items-li * {
    overflow: visible !important;
}

.ve-dropdown-items-li label,
.ve-dropdown-items-li span,
.ve-dropdown-items-li div {
    overflow: visible !important;
    text-overflow: clip !important;
}

.ve-dropdown-items-li .ve-checkbox {
    overflow: visible !important;
}

.ve-dropdown-items-li .ve-checkbox-label {
    overflow: visible !important;
    white-space: normal !important;
}

/* Responsive for all zoom levels - using viewport-based sizing */
@media screen and (max-width: 1920px) {
    .ve-dropdown-items {
        max-height: min(50vh, 400px) !important;
    }
}

@media screen and (max-width: 1600px) {
    .ve-dropdown-items {
        max-height: min(45vh, 380px) !important;
    }
}

@media screen and (max-width: 1366px) {
    .ve-dropdown-items {
        max-height: min(40vh, 350px) !important;
    }
}

@media screen and (max-width: 1024px) {
    .ve-dropdown-items {
        max-height: min(35vh, 320px) !important;
    }
}

@media screen and (max-width: 960px) {
    /* Captures 200% zoom on 1920px screens (1920/2 = 960px) */
    .ve-dropdown-items {
        max-height: min(30vh, 250px) !important;
        max-width: 400px !important;
    }
}

@media screen and (max-width: 800px) {
    /* Captures 200% zoom on 1600px screens */
    .ve-dropdown-items {
        max-height: min(28vh, 220px) !important;
        max-width: 380px !important;
    }
}

@media screen and (max-width: 683px) {
    /* Captures 200% zoom on 1366px screens */
    .ve-dropdown-items {
        max-height: min(25vh, 200px) !important;
        max-width: 350px !important;
    }
}

@media screen and (max-width: 600px) {
    .ve-dropdown-items {
        max-height: min(22vh, 180px) !important;
        max-width: 320px !important;
    }
}

.table-header-cell-class {
    background: #004747 !important;
    color: #fff !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    padding: 0.1rem 0 !important;
    padding-left: 0.3rem !important;
    height: max-content !important;
    vertical-align: middle !important;
    text-align: left !important;
}

.table-header-cell-class span {
    display: inline-flex !important;
    align-items: center;
}

.table-header-cell-class span i:first-child {
    top: -3px !important;
}

.table-header-cell-class span i:last-child {
    top: 6.5px !important;
}

.table-header-cell-class .ve-table-filter-icon i {
    color: #c5c5c5 !important;
}

.priorizar-btn,
.justificar-btn {
    cursor: pointer;
}

.priorizar-btn span {
    background-color: #00A39F;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #fff;
}

.justificar-btn span {
    background-color: #FA394D;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #fff;
}

.justificar-label span {
    background-color: #fff4f6;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #FA394D;
    cursor: pointer;
}

.priorizar-label span {
    background-color: #ebf8f8;
    border-radius: 0.2rem;
    font-weight: 600;
    color: #00A39F;
}

.eficaz-class span {
    font-weight: 600 !important;
    color: #207B0A !important;
    background-color: #c1f7c7 !important;
    border-radius: 0.2rem;
}

.ineficaz-class span {
    font-weight: 600 !important;
    color: #7B0A0C !important;
    background-color: #f7c1c2 !important;
    border-radius: 0.2rem;
}

.pending-class span {
    font-weight: 600 !important;
    color: #7B3F0A !important;
    background-color: #F7EFC1 !important;
    border-radius: 0.2rem;
}

.rta-code-class span {
    font-weight: 600 !important;
    color: #0A4D7B !important;
    background-color: #C1E3F7 !important;
    border-radius: 0.2rem;
}

.justified-class span {
    font-weight: 600 !important;
    color: #5A5A5A !important;
    background-color: #E0E0E0 !important;
    border-radius: 0.2rem;
}

.record-clickable {
    cursor: pointer !important;
}

.record-clickable:hover {
    opacity: 0.8;
}

.open-new-tab-icon {
    cursor: pointer;
    color: #004747;
    font-size: 1.1rem;
    transition: color 0.2s ease;
}

.open-new-tab-icon:hover {
    color: #00A39F;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 12rem;
  margin-top: 12rem;
  margin-bottom: 14rem;
}

.empty-state-content {
  text-align: center;
  max-width: 500px;
}

.empty-state-content h4 {
  color: #004747;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.empty-state-content p {
  color: #6c757d;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}
</style>
